import React from 'react';
import { DollarSign, Check, Wrench, ChevronRight } from '../lib/icons';
import { Link } from 'react-router-dom';
import ContentSlot from './ContentSlot';

const CATEGORIES = [
  { emoji: '🎮', name: 'PS5',              sub: 'All models' },
  { emoji: '🟢', name: 'Xbox Series',      sub: 'X & S' },
  { emoji: '🕹️', name: 'Nintendo Switch',  sub: 'All models + Switch 2' },
  { emoji: '💻', name: 'Steam Deck',       sub: 'LCD & OLED' },
  { emoji: '🖥️', name: 'ROG Ally',         sub: 'Ally & Ally X' },
  { emoji: '📱', name: 'iPhone & iPad',    sub: 'All generations' },
  { emoji: '💻', name: 'MacBook',          sub: 'Air & Pro, all chips' },
  { emoji: '👾', name: 'Game Boy / 3DS',   sub: 'All retro handhelds' },
];

const BROKEN_ISSUES = [
  { issue: 'No power / won\'t turn on',     devices: 'All consoles' },
  { issue: 'HDMI port broken / no video',   devices: 'PS5, Xbox, Switch' },
  { issue: 'Cracked or broken screen',      devices: 'Switch, 3DS, Steam Deck' },
  { issue: 'Won\'t charge',                 devices: 'Switch, Steam Deck, iPhone' },
  { issue: 'Overheating / shuts off',       devices: 'PS5, Xbox, Switch' },
  { issue: 'Failed DIY repair',             devices: 'Any device' },
  { issue: 'Water / liquid damage',         devices: 'Any device' },
  { issue: 'iCloud or account locked',      devices: 'iPhone, iPad, MacBook' },
];

const DeviceBuying = () => (
  <section id="buying" className="py-20 bg-gradient-to-b from-black to-[#0D0D0D]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      {/* Header */}
      <div className="text-center mb-14">
        <div className="inline-flex items-center space-x-2 bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-full px-6 py-2 mb-6">
          <DollarSign className="h-4 w-4 text-[#39FF14]" />
          <span className="text-[#39FF14] font-semibold text-sm">
            <ContentSlot slotId="buying-badge" fallback="CASH FOR CONSOLES" contentType="label" pageSection="device-buying" label="Section Badge" />
          </span>
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-[#E0E0E0] mb-4">
          <ContentSlot slotId="buying-heading" fallback="We Buy Gaming Devices" contentType="heading" pageSection="device-buying" label="Section Heading" />
        </h2>
        <ContentSlot
          slotId="buying-subtitle"
          fallback="Working or broken — get instant cash for your console, handheld, or Apple device."
          contentType="paragraph"
          as="p"
          pageSection="device-buying"
          label="Section Subtitle"
          className="text-xl text-[#B0B0B0] max-w-2xl mx-auto"
        />
      </div>

      {/* Category tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
        {CATEGORIES.map(cat => (
          <div
            key={cat.name}
            className="flex flex-col items-center justify-center gap-1.5 p-5 rounded-2xl bg-gray-900 border border-gray-800 hover:border-[#39FF14]/40 transition-all"
          >
            <span className="text-3xl">{cat.emoji}</span>
            <p className="text-sm font-bold text-[#E0E0E0] text-center leading-tight">{cat.name}</p>
            <p className="text-xs text-gray-500 text-center">{cat.sub}</p>
          </div>
        ))}
      </div>

      {/* Get a quote CTA */}
      <div className="text-center mb-16">
        <Link
          to="/buyback"
          className="inline-flex items-center gap-2 bg-[#39FF14] text-[#0D0D0D] px-8 py-3 rounded-xl font-bold hover:bg-white transition-colors"
        >
          Get an Instant Estimate <ChevronRight className="h-4 w-4" />
        </Link>
        <p className="text-xs text-gray-500 mt-2">Select your device and get a price in under a minute</p>
      </div>

      {/* Broken consoles */}
      <div className="bg-gradient-to-br from-[#FF1744]/10 to-transparent border border-[#FF1744]/30 rounded-2xl p-8 md:p-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 bg-[#FF1744]/20 border border-[#FF1744]/40 rounded-full px-5 py-1.5 mb-4">
            <Wrench className="h-4 w-4 text-[#FF1744]" />
            <span className="text-[#FF1744] font-semibold text-sm">BROKEN CONSOLES WELCOME</span>
          </div>
          <h3 className="text-3xl font-bold text-[#E0E0E0] mb-3">
            <ContentSlot slotId="buying-broken-heading" fallback="Broken? We Still Buy It" contentType="subheading" pageSection="device-buying" label="Broken Devices Heading" />
          </h3>
          <ContentSlot
            slotId="buying-broken-subtitle"
            fallback="Don't trash it. We pay fair prices for devices with common issues — even failed DIY repairs."
            contentType="paragraph"
            as="p"
            pageSection="device-buying"
            label="Broken Devices Subtitle"
            className="text-[#B0B0B0] max-w-xl mx-auto"
          />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {BROKEN_ISSUES.map(item => (
            <div key={item.issue} className="flex items-start gap-3 bg-gray-900/60 rounded-xl p-4 border border-gray-800">
              <Check className="h-4 w-4 text-[#FF1744] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-[#E0E0E0] leading-tight">{item.issue}</p>
                <p className="text-xs text-gray-500 mt-0.5">{item.devices}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Trust strip */}
        <div className="flex flex-wrap justify-center gap-6 text-sm text-[#B0B0B0]">
          {['Instant cash or Zelle/Venmo', 'No appointment needed', 'Fair market pricing', 'Working or broken'].map(item => (
            <div key={item} className="flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-[#39FF14]" />
              {item}
            </div>
          ))}
        </div>
      </div>

    </div>
  </section>
);

export default DeviceBuying;
